// Вътрешен чат по обява (наемател ↔ публикувал обявата).

import React, { useCallback, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { showError } from '../toast';
import { Button, Card, COLORS, Input, Muted, Screen, Title } from '../ui';
import { getLocale, t } from '../i18n';

export default function ChatScreen({ route }: any) {
  const { user } = useAuth();
  const { listingId, title } = route.params;
  const [conversationId, setConversationId] = useState<string | null>(route.params.conversationId ?? null);
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  // silent: при автоматичното опресняване не изкарваме грешка на екрана —
  // мигнала мрежа не бива да прекъсва писането.
  const loadMessages = useCallback(
    (id: string, silent = false) => {
      api(`/listings/conversations/${id}/messages`)
        .then((r) => setMessages(r.messages))
        .catch((e) => {
          if (!silent) showError(e.message);
        });
    },
    []
  );

  // Ако идваме от обява (без разговор) — проверяваме дали вече има започнат
  useEffect(() => {
    if (conversationId) return;
    api('/listings/conversations')
      .then((r) => {
        const existing = r.conversations.find((c: any) => c.listing.id === listingId);
        if (existing) setConversationId(existing.id);
      })
      .catch(() => {});
  }, [conversationId, listingId]);

  // Докато чатът е отворен, се опреснява сам на 10 секунди — иначе новото
  // съобщение от отсрещната страна се вижда чак след излизане и връщане.
  // Таймерът спира при напускане на екрана (useFocusEffect чисти след себе си).
  useFocusEffect(
    useCallback(() => {
      if (!conversationId) return;
      loadMessages(conversationId);
      const timer = setInterval(() => loadMessages(conversationId, true), 10_000);
      return () => clearInterval(timer);
    }, [conversationId, loadMessages])
  );

  const send = async () => {
    setSending(true);
    try {
      if (conversationId) {
        await api(`/listings/conversations/${conversationId}/messages`, { body: { text } });
        loadMessages(conversationId);
      } else {
        const res = await api(`/listings/${listingId}/contact`, { body: { text } });
        setConversationId(res.conversation.id);
        loadMessages(res.conversation.id);
      }
      setText('');
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Screen>
      <Title>{title ?? t('Разговор')}</Title>
      {messages.length === 0 && (
        <Card>
          <Muted>{t('Напишете първото съобщение — другата страна ще получи известие.')}</Muted>
        </Card>
      )}
      {messages.map((m) => {
        const mine = m.sender.id === user?.id;
        return (
          <View
            key={m.id}
            style={{
              alignSelf: mine ? 'flex-end' : 'flex-start',
              backgroundColor: mine ? COLORS.primary : COLORS.card,
              borderRadius: 14,
              paddingVertical: 8,
              paddingHorizontal: 12,
              marginBottom: 8,
              maxWidth: '80%',
              borderWidth: mine ? 0 : 1,
              borderColor: COLORS.border,
            }}
          >
            {!mine && (
              <Text style={{ fontSize: 11, color: COLORS.muted }}>
                {m.sender.firstName} {m.sender.lastName}
              </Text>
            )}
            <Text style={{ color: mine ? '#fff' : COLORS.text, fontSize: 15 }}>{m.text}</Text>
            <Text style={{ fontSize: 10, color: mine ? 'rgba(255,255,255,0.7)' : COLORS.muted, marginTop: 2 }}>
              {new Date(m.createdAt).toLocaleTimeString(getLocale() === 'en' ? 'en-GB' : 'bg-BG', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        );
      })}

      <Input value={text} onChangeText={setText} placeholder={t('Съобщение…')} multiline />
      <Button title={t('Изпрати')} onPress={send} disabled={!text.trim()} loading={sending} />
    </Screen>
  );
}
